// WELFARE / SAFETY-TIMER BANNER for the Dispatch board — overdue units with stage
// escalation, audible alerts and one-tap documented resolve. Feb 2026: AUTO checks
// fire only for units on HIGH-RISK calls; for everything else the dispatcher sets a
// manual per-unit timer here (same escalation engine, auto-cancels when unit answers).
import React, { useCallback, useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import { toast } from "sonner";
import { hazardTone, emergencyAlarm, speak } from "@/lib/audio";
import { ShieldAlert, CheckCircle2, Timer, X } from "lucide-react";

const STAGE_STYLE = {
  1: { bg: "#2b1c04", border: "#F59E0B", chip: "#F59E0B", label: "STAGE 1 · UNIT NOTIFIED" },
  2: { bg: "#2b1204", border: "#F97316", chip: "#F97316", label: "STAGE 2 · SUPERVISORS NOTIFIED" },
  3: { bg: "#3a0f13", border: "#EF4444", chip: "#EF4444", label: "STAGE 3 · COMMAND ESCALATION" },
};

export default function WelfareBanner() {
  const [overdue, setOverdue] = useState([]);
  const [manual, setManual] = useState([]);
  const [denied, setDenied] = useState(false);
  const [units, setUnits] = useState([]);
  const [pickUnit, setPickUnit] = useState("");
  const [mins, setMins] = useState(10);
  const alerted = useRef(new Set()); // "checkId:stage" keys already sounded

  const load = useCallback(() => {
    client.get("/safety-timers")
      .then((r) => { setOverdue(r.data.overdue || []); setManual(r.data.manual_timers || []); })
      .catch((e) => { if (e.response?.status === 403) setDenied(true); });
  }, []);
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, [load]);
  useEffect(() => { client.get("/units").then((r) => setUnits(r.data)).catch(() => {}); }, []);
  useRealtime(["welfare"], load);

  // Audible alerting: hazard tone per new stage-1/2, full emergency alarm + voice on stage 3.
  useEffect(() => {
    overdue.forEach((o) => {
      const key = `${o.check_id}:${o.stage}`;
      if (alerted.current.has(key)) return;
      alerted.current.add(key);
      try {
        if (o.stage >= 3) { emergencyAlarm(); speak(`Welfare emergency. ${o.unit_id} unresponsive ${o.minutes} minutes.`); }
        else hazardTone();
      } catch { /* audio may be blocked pre-interaction */ }
    });
  }, [overdue]);

  const resolve = async (o) => {
    const note = window.prompt(`Resolution note for ${o.unit_id} welfare check (required):`);
    if (!note?.trim()) return;
    try {
      await client.post(`/safety-timers/${o.check_id}/resolve`, { note: note.trim() });
      toast.success(`Welfare check resolved & documented — ${o.unit_id}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not resolve"); }
  };

  const setTimer = async () => {
    if (!pickUnit) { toast.error("Pick a unit for the welfare timer"); return; }
    try {
      await client.post("/safety-timers/manual", { unit_id: pickUnit, minutes: Number(mins) });
      toast.success(`Welfare timer set — ${pickUnit}, ${mins} min (auto-cancels when the unit answers)`);
      setPickUnit(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not set timer"); }
  };

  const cancelTimer = async (t) => {
    try { await client.delete(`/safety-timers/manual/${t.unit_id}`); toast.success(`Timer cancelled — ${t.unit_id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };

  if (denied) return null;
  return (
    <div data-testid="welfare-banner" className="shrink-0 divide-y divide-[#1E1F24]">
      {/* dispatcher-set manual timers: for units NOT on high-risk calls */}
      <div data-testid="welfare-timer-bar" className="px-4 py-1.5 flex items-center gap-2 flex-wrap bg-[#0d0e11] border-b border-[#2C2D35]">
        <Timer size={13} className="text-[#F59E0B]" />
        <span className="font-mono text-[9px] text-[#9CA3AF] uppercase">Welfare Timer — auto only on high-risk calls · set one for any other unit:</span>
        <select data-testid="welfare-timer-unit" value={pickUnit} onChange={(e) => setPickUnit(e.target.value)}
          className="bg-[#141518] border border-[#2C2D35] px-1.5 py-0.5 text-[11px] font-mono outline-none focus:border-[#F59E0B] text-white max-w-[220px]">
          <option value="">Unit / officer…</option>
          {units.map((u) => <option key={u.unit_id} value={u.unit_id}>{u.unit_id}{u.personnel ? ` — ${u.personnel}` : ""}</option>)}
        </select>
        <select data-testid="welfare-timer-mins" value={mins} onChange={(e) => setMins(e.target.value)}
          className="bg-[#141518] border border-[#2C2D35] px-1.5 py-0.5 text-[11px] font-mono outline-none text-white">
          <option value="5">5 min</option><option value="10">10 min</option><option value="15">15 min</option>
          <option value="20">20 min</option><option value="30">30 min</option><option value="45">45 min</option>
          <option value="60">60 min</option><option value="90">90 min</option>
        </select>
        <button data-testid="welfare-timer-set" onClick={setTimer}
          className="px-2 py-0.5 text-[10px] font-head font-bold bg-[#F59E0B] text-black hover:bg-[#d97706]">SET TIMER</button>
        {manual.map((t) => (
          <span key={t.id} data-testid={`welfare-manual-${t.unit_id}`}
            className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono border border-[#F59E0B]/60 text-[#F59E0B] rounded-sm">
            <Timer size={10} /> {t.unit_id} · {t.seconds_left != null ? `${Math.floor(t.seconds_left / 60)}:${String(t.seconds_left % 60).padStart(2, "0")}` : `${t.minutes}m`}
            <button data-testid={`welfare-manual-cancel-${t.unit_id}`} onClick={() => cancelTimer(t)} title="Cancel timer"
              className="ml-0.5 text-[#9CA3AF] hover:text-[#EF4444]"><X size={10} /></button>
          </span>
        ))}
      </div>

      {overdue.map((o) => {
        const s = STAGE_STYLE[o.stage] || STAGE_STYLE[1];
        return (
          <div key={o.check_id} data-testid={`welfare-banner-${o.unit_id}`}
            className="px-4 py-2 flex items-center gap-3 flex-wrap border-b-2"
            style={{ background: s.bg, borderColor: s.border }}>
            <ShieldAlert size={18} className={o.stage >= 3 ? "animate-pulse" : ""} style={{ color: s.chip }} />
            <span className="font-head text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: s.chip, color: "#0A0A0A" }}>{s.label}</span>
            <span className="font-head font-bold text-sm text-white">{o.unit_id}</span>
            <span className="font-mono text-[10px] text-[#D1D5DB]">
              no answer {o.minutes} min while {(o.status || "").toUpperCase()}
              {o.incident_cc ? ` · on ${o.incident_cc}` : ""}{o.personnel ? ` · ${o.personnel}` : ""}
              {o.origin ? ` · ${o.origin}` : ""}
              {" · auto-clears on status change, radio message or COPY"}
            </span>
            <button data-testid={`welfare-banner-resolve-${o.unit_id}`} onClick={() => resolve(o)}
              className="ml-auto shrink-0 px-3 py-1.5 rounded font-head font-bold text-[10px] text-black flex items-center gap-1"
              style={{ background: s.chip }}>
              <CheckCircle2 size={13} /> RESOLVE (NOTE)
            </button>
          </div>
        );
      })}
    </div>
  );
}
